import { useEffect, useState } from "react";
import { api, formatApiError, resolveBackendAssetUrl } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Eye, EyeOff, Upload, Image as ImageIcon, X } from "lucide-react";

const EMPTY = { title: "", message: "", image_url: "", active: true };

export default function AdminAvisos() {
  const [alerts, setAlerts] = useState([]);
  const [n, setN] = useState(EMPTY);
  const [newImageFile, setNewImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/alerts").then((r) => setAlerts(r.data));
  useEffect(() => { load(); }, []);

  const uploadImage = async (file) => {
    if (!file) return "";
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Maximo 6 MB");
      throw new Error("La imagen supera el maximo de 6 MB");
    }
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/alerts/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.url;
  };

  const create = async () => {
    if (!n.title || !n.message) return toast.error("Completa titulo y mensaje");
    setUploading(true);
    try {
      const image_url = newImageFile ? await uploadImage(newImageFile) : n.image_url;
      await api.post("/alerts", { ...n, image_url });
      setN(EMPTY);
      setNewImageFile(null);
      load();
      toast.success("Destacado publicado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async (a) => {
    setUploading(a.id);
    try {
      const image_url = a._imageFile ? await uploadImage(a._imageFile) : (a.image_url || "");
      await api.put(`/alerts/${a.id}`, {
        title: a.title,
        message: a.message,
        image_url,
        active: a.active,
      });
      toast.success("Guardado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Eliminar destacado?")) return;
    await api.delete(`/alerts/${id}`);
    load();
  };

  const updateAlert = (id, patch) => {
    setAlerts((arr) => arr.map((x) => x.id === id ? { ...x, ...patch } : x));
  };

  return (
    <div className="space-y-6" data-testid="admin-avisos">
      <header>
        <h1 className="font-display font-black text-3xl">Hoy / Destacados</h1>
        <p className="text-muted-foreground">Mensajes destacados que apareceran en la web publica.</p>
      </header>

      <div className="bg-white rounded-2xl border border-border p-5 space-y-3" data-testid="avisos-create">
        <h2 className="font-display font-bold">Nuevo destacado</h2>
        <input placeholder="Titulo" value={n.title} onChange={(e) => setN({ ...n, title: e.target.value })} className="w-full rounded-lg border border-border px-3 py-2" data-testid="aviso-new-title" />
        <textarea placeholder="Mensaje" rows={3} value={n.message} onChange={(e) => setN({ ...n, message: e.target.value })} className="w-full rounded-lg border border-border px-3 py-2" data-testid="aviso-new-message" />
        <ImagePicker
          imageUrl={n.image_url}
          file={newImageFile}
          onFile={setNewImageFile}
          onRemove={() => {
            setN({ ...n, image_url: "" });
            setNewImageFile(null);
          }}
          testid="aviso-new-image"
        />
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={n.active} onChange={(e) => setN({ ...n, active: e.target.checked })} data-testid="aviso-new-active" /> Activo
        </label>
        <div>
          <button onClick={create} disabled={!!uploading} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full font-bold disabled:opacity-60" data-testid="aviso-add-btn">
            <Plus size={16} /> {uploading === true ? "Publicando..." : "Publicar"}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {alerts.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-border p-4 space-y-3" data-testid={`aviso-row-${a.id}`}>
            <div className="flex items-center justify-between gap-2">
              <input value={a.title} onChange={(e) => updateAlert(a.id, { title: e.target.value })} className="flex-1 rounded-lg border border-border px-2 py-1.5 font-bold" />
              <label className="inline-flex items-center gap-1 text-xs">
                <input type="checkbox" checked={a.active} onChange={(e) => updateAlert(a.id, { active: e.target.checked })} />
                {a.active ? <Eye size={14} /> : <EyeOff size={14} />}
              </label>
            </div>
            <textarea rows={2} value={a.message} onChange={(e) => updateAlert(a.id, { message: e.target.value })} className="w-full rounded-lg border border-border px-2 py-1.5 text-sm" />
            <ImagePicker
              imageUrl={a.image_url}
              file={a._imageFile}
              onFile={(file) => updateAlert(a.id, { _imageFile: file })}
              onRemove={() => updateAlert(a.id, { image_url: "", _imageFile: null })}
              testid={`aviso-image-${a.id}`}
            />
            <div className="flex gap-2">
              <button onClick={() => save(a)} disabled={uploading === a.id} className="inline-flex items-center gap-1 bg-primary text-primary-foreground px-3 py-1.5 rounded-full text-sm disabled:opacity-60" data-testid={`aviso-save-${a.id}`}>
                <Save size={14} /> {uploading === a.id ? "Guardando..." : "Guardar"}
              </button>
              <button onClick={() => remove(a.id)} className="inline-flex items-center gap-1 bg-destructive text-destructive-foreground px-3 py-1.5 rounded-full text-sm" data-testid={`aviso-delete-${a.id}`}><Trash2 size={14} /> Eliminar</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImagePicker({ imageUrl, file, onFile, onRemove, testid }) {
  const previewUrl = resolveBackendAssetUrl(imageUrl);

  return (
    <div className="rounded-xl border border-border bg-accent/30 p-3 space-y-3" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="relative h-20 w-28 shrink-0 overflow-hidden rounded-lg bg-white border border-border">
          {previewUrl ? (
            <img src={previewUrl} alt="Imagen del destacado" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full grid place-items-center text-muted-foreground">
              <ImageIcon size={24} />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="text-sm font-medium">Imagen opcional</div>
          <div className="text-xs text-muted-foreground truncate">
            {file ? file.name : (imageUrl ? "Imagen guardada" : "Sin imagen")}
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1 bg-primary text-primary-foreground px-3 py-1.5 rounded-full text-xs font-bold hover:brightness-110 transition">
              <Upload size={13} /> Seleccionar
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const selected = e.target.files?.[0];
                  if (selected) onFile(selected);
                  e.target.value = "";
                }}
              />
            </label>
            {(imageUrl || file) && (
              <button type="button" onClick={onRemove} className="inline-flex items-center gap-1 bg-white border border-border px-3 py-1.5 rounded-full text-xs font-medium hover:bg-accent transition">
                <X size={13} /> Quitar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
